import { unreachable } from 'module/helpers/unreachable';
import { TranslationKey, useTranslation } from 'module/hooks/use-translation';
import {
  faBriefcaseBlank,
  faHandshake,
  faListUl,
  faMagnifyingGlass,
  faQuestionCircle,
  faSignal,
} from '@fortawesome/pro-regular-svg-icons';
import { Editor, Range } from '@tiptap/core';
import {
  CustomSectionType,
  CustomSectionContentState,
  CustomSectionState,
} from 'api/custom-section/types';
import { PageType } from 'api/page-template/types';
import { MeetingTemplateSection } from 'api/pages';
import { useGetTemplateSectionsQuery } from 'queries/pages';
import { useMemo } from 'react';
import { CommandListItemIcon } from './common';
import { isAllowedOnlyAtFirstLevel } from './utils';
import { CommandPickerItem, CommandPickerLeafItem } from '../types';

const sectionIntoAttributes = (section: MeetingTemplateSection) => {
  const { sectionType } = section;
  const baseAttributes = {
    contentState: CustomSectionContentState.Prompt,
    state: CustomSectionState.Open,
    title: section.title,
  };

  switch (sectionType) {
    case CustomSectionType.Question:
    case CustomSectionType.Action:
    case CustomSectionType.ExtractInfo:
    case CustomSectionType.SignalDetails:
    case CustomSectionType.MeetingDetails:
    case CustomSectionType.AccountDetails:
    case CustomSectionType.CustomTextSection:
    case CustomSectionType.CompanyPageSourceDerivedSection:
      return {
        ...baseAttributes,
        sectionType,
        config: section.config,
      };
    default:
      return unreachable(sectionType);
  }
};

const insertSection =
  (section: MeetingTemplateSection) => (editor: Editor, range: Range) => {
    return editor
      .chain()
      .focus()
      .addCustomSection({
        insertAt: range.from,
        attributes: sectionIntoAttributes(section),
      })
      .run();
  };

const iconForSectionType = (sectionType: CustomSectionType) => {
  switch (sectionType) {
    case CustomSectionType.Action:
      return <CommandListItemIcon icon={faListUl} />;
    case CustomSectionType.Question:
      return <CommandListItemIcon icon={faQuestionCircle} />;
    case CustomSectionType.SignalDetails:
      return <CommandListItemIcon icon={faSignal} />;
    case CustomSectionType.ExtractInfo:
      return <CommandListItemIcon icon={faMagnifyingGlass} />;
    case CustomSectionType.AccountDetails:
      return <CommandListItemIcon icon={faBriefcaseBlank} />;
    case CustomSectionType.MeetingDetails:
      return <CommandListItemIcon icon={faHandshake} />;
  }
};

export const sectionAsCommandItem = (
  section: MeetingTemplateSection,
  index: number,
): CommandPickerLeafItem => {
  return {
    id: `${section.identifier}_${index}`,
    label: section.title,
    icon: iconForSectionType(section.sectionType),
    execute: insertSection(section),
    isAllowed: isAllowedOnlyAtFirstLevel,
  };
};

export const useGetTemplateSectionsCommands = ({
  pageType,
}: {
  pageType?: PageType;
}): {
  items: CommandPickerItem[];
  isLoading: boolean;
} => {
  const { data: sections, isLoading } = useGetTemplateSectionsQuery(
    pageType ?? PageType.Meeting,
  );

  const label = useTranslation(
    `template_sections.${(pageType ?? PageType.Meeting).toLowerCase()}` as TranslationKey,
  );

  return useMemo(() => {
    return {
      items: [
        {
          id: 'template-section',
          label: label,
          items: (sections ?? []).map((section, index) => ({
            id: `${section.identifier}_${index}`,
            label: section.title,
            icon: iconForSectionType(section.sectionType),
            execute: insertSection(section),
            isAllowed: isAllowedOnlyAtFirstLevel,
          })),
        },
      ],
      isLoading,
    };
  }, [sections, isLoading, label]);
};
